import axios from "axios";
import { loanBackendPostUrl } from "../config/taskApi";

const DEBOUNCE_MS = 650;
const SAVE_ENQUIRY_ENDPOINT = "saveEnquiry.php";

let debounceTimer = null;
let pendingOverrides = null;
let pendingStep = "";
let autosavePaused = false;
let unloadBound = false;

const normalizeMobile = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(-10);

const readJson = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const pickName = (loan, aadhaar, overrides) => {
  const fromOverride =
    overrides.Name ||
    overrides.name ||
    overrides.first_name ||
    overrides.firstName ||
    "";
  if (fromOverride) return String(fromOverride).trim();

  const parts = [
    loan.first_name || loan.firstName || "",
    loan.middle_name || loan.middleName || "",
    loan.last_name || loan.lastName || "",
  ]
    .map((p) => String(p || "").trim())
    .filter(Boolean);

  return (
    String(loan.name || loan.full_name || loan.pan_fullName || "").trim() ||
    parts.join(" ") ||
    String(aadhaar?.name || "").trim() ||
    ""
  );
};

const pickPan = (loan, overrides) =>
  String(
    overrides.pan ||
      overrides.pancard ||
      loan.pan ||
      loan.pancard ||
      loan.pan_verifiedPan ||
      ""
  )
    .toUpperCase()
    .trim();

const pickAadhaar = (loan, aadhaar, overrides) =>
  String(
    overrides.aadhaar ||
      overrides.aadhaar_number ||
      loan.aadhaar ||
      loan.aadhaar_number ||
      aadhaar?.aadhaarNumber ||
      aadhaar?.aadhaar_number ||
      localStorage.getItem("aadhaarNumber") ||
      ""
  ).replace(/\D/g, "");

const pickEmail = (loan, overrides) =>
  String(
    overrides.email ||
      overrides.email_personal ||
      loan.email ||
      loan.email_personal ||
      ""
  ).trim();

const pickLoanAmount = (loan, overrides) =>
  String(
    overrides.loan_amount ||
      overrides.loanAmount ||
      loan.loan_amount ||
      loan.loanAmount ||
      ""
  ).replace(/[^\d.]/g, "");

const pickCity = (loan, overrides) => {
  const cityId = String(
    overrides.city_id || overrides.cityId || loan.city_id || loan.cityId || ""
  ).trim();
  const cityName = String(
    overrides.city_name ||
      overrides.city ||
      loan.city_name ||
      loan.city ||
      loan.district ||
      ""
  ).trim();
  return { cityId, cityName };
};

const isMobileVerified = (overrides) => {
  if (
    overrides.mobile_verified === 1 ||
    overrides.mobile_verified === "1" ||
    overrides.mobile_verified === true
  ) {
    return 1;
  }
  if (
    overrides.mobile_verified === 0 ||
    overrides.mobile_verified === "0" ||
    overrides.mobile_verified === false
  ) {
    return 0;
  }
  return localStorage.getItem("mobile_verified") === "true" ? 1 : 0;
};

/**
 * Build cumulative LMS enquiry payload from storage + call overrides.
 */
export const buildEnquiryPayload = (overrides = {}) => {
  const loan = readJson("loanFormData", {}) || {};
  const aadhaar = readJson("aadhaarData", null);

  const mobile = normalizeMobile(
    overrides.Mobile ||
      overrides.mobile ||
      localStorage.getItem("mobileNumber") ||
      loan.mobile ||
      ""
  );

  const name = pickName(loan, aadhaar, overrides);
  const pan = pickPan(loan, overrides);
  const aadhaarNumber = pickAadhaar(loan, aadhaar, overrides);
  const email = pickEmail(loan, overrides);
  const loanAmount = pickLoanAmount(loan, overrides);
  const { cityId, cityName } = pickCity(loan, overrides);
  const step = String(overrides.step || pendingStep || "").trim();

  const payload = {
    Mobile: mobile,
    mobile,
    mobile_verified: isMobileVerified(overrides),
    step,
  };

  if (name) {
    payload.Name = name;
    payload.first_name = name;
    payload.name = name;
  }
  if (pan) {
    payload.pan = pan;
    payload.pancard = pan;
  }
  if (aadhaarNumber) {
    payload.aadhaar = aadhaarNumber;
    payload.aadhaar_number = aadhaarNumber;
  }
  if (email) {
    payload.email = email;
    payload.email_personal = email;
  }
  if (loanAmount) {
    payload.loan_amount = loanAmount;
  }
  if (cityId) payload.city_id = cityId;
  if (cityName) {
    payload.city_name = cityName;
    payload.city = cityName;
  }

  // --- Optional draft extras (stored in cust_enquiry_remarks on backend) ---
  const put = (key, value) => {
    if (value === undefined || value === null) return;
    const text = typeof value === "string" ? value.trim() : value;
    if (text === "" || text === false) return;
    payload[key] = text;
  };

  const fromLoanOrOverride = (...keys) => {
    for (const key of keys) {
      if (overrides[key] !== undefined && overrides[key] !== null && overrides[key] !== "") {
        return overrides[key];
      }
      if (loan[key] !== undefined && loan[key] !== null && loan[key] !== "") {
        return loan[key];
      }
    }
    return undefined;
  };

  put("latitude", fromLoanOrOverride("latitude", "lat"));
  put("longitude", fromLoanOrOverride("longitude", "lng", "lon"));
  put(
    "coordinates",
    fromLoanOrOverride("coordinates", "geo_coordinates")
  );
  if (!payload.coordinates && payload.latitude && payload.longitude) {
    payload.coordinates = `${payload.latitude},${payload.longitude}`;
  }

  put("aadhaar_name", fromLoanOrOverride("aadhaar_name", "aadhaarName"));
  put("aadhaar_dob", fromLoanOrOverride("aadhaar_dob", "aadhaarDob", "dob"));
  put(
    "aadhaar_gender",
    fromLoanOrOverride("aadhaar_gender", "aadhaarGender", "gender")
  );

  put("employer", fromLoanOrOverride("employer", "employer_name", "company_name"));
  put("monthly_income", fromLoanOrOverride("monthly_income", "monthlyIncome", "income"));
  put("salary_date", fromLoanOrOverride("salary_date", "salaryDate"));
  put("loan_purpose", fromLoanOrOverride("loan_purpose", "loanPurpose", "purpose"));
  put("employment_type", fromLoanOrOverride("employment_type", "employmentType"));
  put("salary_mode", fromLoanOrOverride("salary_mode", "salaryMode"));

  put("bank_name", fromLoanOrOverride("bank_name", "bankName"));
  put(
    "account_holder_name",
    fromLoanOrOverride("account_holder_name", "accountHolderName")
  );
  put("account_number", fromLoanOrOverride("account_number", "accountNumber"));
  put("ifsc", fromLoanOrOverride("ifsc", "IFSC"));
  put(
    "customer_banking_id",
    fromLoanOrOverride("customer_banking_id", "customerBankingId")
  );

  const refs = overrides.references || loan.references;
  if (Array.isArray(refs) && refs.length) {
    payload.references = refs;
  }

  // Never send selfie base64 — only completion flag
  if (
    overrides.selfie_completed === 1 ||
    overrides.selfie_completed === true ||
    overrides.selfie_completed === "1" ||
    step === "selfie_completed"
  ) {
    payload.selfie_completed = 1;
  }

  const stateId = fromLoanOrOverride("state_id", "stateId");
  if (stateId) payload.state_id = stateId;
  const pin = fromLoanOrOverride("pincode", "pin");
  if (pin) {
    payload.pincode = pin;
    payload.pin = pin;
  }

  const utm =
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("utm_source")) ||
    localStorage.getItem("utm_source") ||
    "";
  if (utm) payload.utm_source = utm;

  payload.source = overrides.source || "APPLY-NOW";

  return payload;
};

const rememberEnquiryId = (data) => {
  if (!data || typeof data !== "object") return;
  const enquiryId =
    data.cust_enquiry_id ||
    data.enquiry_id ||
    data.enquiryId ||
    data.id ||
    data.Data?.cust_enquiry_id ||
    null;
  if (!enquiryId) return;
  try {
    localStorage.setItem("earlyEnquiryId", String(enquiryId));
    sessionStorage.setItem("applyFlowEarlyEnquiryId", String(enquiryId));
  } catch {
    /* ignore */
  }
};

const saveEnquiryUrl = () => loanBackendPostUrl(SAVE_ENQUIRY_ENDPOINT);

const postWithKeepalive = (payload) => {
  const url = saveEnquiryUrl();
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    /* fall through */
  }

  try {
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        mode: "cors",
        credentials: "omit",
      }).catch((err) => {
        console.warn("saveEnquiry keepalive failed", err?.message || err);
      });
    }
  } catch (err) {
    console.warn("saveEnquiry keepalive failed", err?.message || err);
  }
};

/**
 * Fire-and-forget POST to saveEnquiry.php. Never throws to callers.
 */
export const saveEnquiryNow = async (overrides = {}, options = {}) => {
  const { keepalive = false } = options;

  if (autosavePaused && !keepalive) return null;

  try {
    const payload = buildEnquiryPayload(overrides);
    if (!payload.Mobile || payload.Mobile.length !== 10) {
      return null;
    }

    if (keepalive) {
      postWithKeepalive(payload);
      return null;
    }

    const resp = await axios.post(saveEnquiryUrl(), payload, {
      headers: { "Content-Type": "application/json" },
      timeout: 15000,
      validateStatus: () => true,
    });

    if (resp?.status >= 400) {
      console.warn("saveEnquiry HTTP", resp.status, resp?.data);
      return null;
    }

    rememberEnquiryId(resp?.data);
    return resp?.data || null;
  } catch (err) {
    console.warn("saveEnquiry failed", err?.message || err);
    return null;
  }
};

const compactOverrides = (overrides = {}) => {
  const out = {};
  Object.keys(overrides || {}).forEach((key) => {
    const value = overrides[key];
    if (value === undefined || value === null || value === "") return;
    out[key] = value;
  });
  return out;
};

/**
 * Debounced autosave (500–800ms). Merges overrides across rapid calls.
 */
export const scheduleSaveEnquiry = (overrides = {}) => {
  if (autosavePaused) return;

  const clean = compactOverrides(overrides);
  pendingOverrides = { ...(pendingOverrides || {}), ...clean };
  if (clean.step) pendingStep = String(clean.step);

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const merged = pendingOverrides || {};
    pendingOverrides = null;
    debounceTimer = null;
    saveEnquiryNow(merged);
  }, DEBOUNCE_MS);
};

/** Flush pending debounced save immediately (e.g. Next click). */
export const flushSaveEnquiry = (overrides = {}) => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  const clean = compactOverrides(overrides);
  const merged = { ...(pendingOverrides || {}), ...clean };
  pendingOverrides = null;
  if (clean.step) pendingStep = String(clean.step);
  return saveEnquiryNow(merged);
};

/** Unload / tab hide — keepalive, no await. */
export const saveEnquiryOnUnload = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  const merged = pendingOverrides || {};
  pendingOverrides = null;
  saveEnquiryNow(merged, { keepalive: true });
};

/** Pause during final lead submit so we never hit saveEnquiry on Submit. */
export const pauseEnquiryAutosave = () => {
  autosavePaused = true;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendingOverrides = null;
};

export const resumeEnquiryAutosave = () => {
  autosavePaused = false;
};

/**
 * Bind pagehide + beforeunload once for apply-loan / apply-now.
 */
export const bindEnquiryUnloadAutosave = () => {
  if (typeof window === "undefined" || unloadBound) {
    return () => {};
  }
  unloadBound = true;
  const onLeave = () => saveEnquiryOnUnload();
  window.addEventListener("pagehide", onLeave);
  window.addEventListener("beforeunload", onLeave);
  return () => {
    window.removeEventListener("pagehide", onLeave);
    window.removeEventListener("beforeunload", onLeave);
    unloadBound = false;
  };
};

export default scheduleSaveEnquiry;
